/* Dispatch 13 -- MobileShell.
 *
 * Slim adaptive shell served to viewports narrower than 768px.
 *   - Top bar: 48px slim (h-12). Logo + Install + Bell + Profile.
 *   - Bottom tab bar: 56px (h-14) + iOS safe-area padding.
 *     Tabs (5, fixed): Home / HR / Tasks / Notes / More.
 *   - "More" -> bottom sheet with all secondary modules from NAV.
 *   - Safe-area handled at both top (env(safe-area-inset-top)) and
 *     bottom (env(safe-area-inset-bottom)) via CSS variables.
 *
 * Desktop code path is preserved in DesktopShell.jsx; this file is
 * consumed only when `useIsMobile()` is true.
 */
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Users,
  CheckSquare,
  StickyNote,
  MoreHorizontal,
  Sun,
  Moon,
  Languages,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/store/auth";
import { useModuleVisibility } from "@/store/moduleVisibility";
import { NAV } from "@/lib/nav";
import { usePermission } from "@/hooks/usePermission";
import { Logo } from "@/components/Logo";
import { NotificationsBell } from "./NotificationsBell";
import { ProfileMenu } from "./ProfileMenu";
import { InstallAppButton } from "./InstallAppButton";
import ModuleGuard from "@/components/ModuleGuard";
import PreviewBanner from "@/components/PreviewBanner";
import PasswordChangeBanner from "@/components/PasswordChangeBanner";
import HelpBotLauncher from "@/components/ai/HelpBotLauncher";
import { GlobalCommandPalette } from "./GlobalCommandPalette";
import { QuickCaptureModal } from "./QuickCaptureModal";

/**
 * Fixed 5-tab set for the mobile bottom bar. Order + labels per
 * Dispatch 13 spec: Home | HR | Tasks | Notes | More.
 * "More" is a Sheet trigger; the other four are Links.
 */
const MOBILE_TABS = [
  { key: "home", moduleKey: "home", labelKey: "nav.home", icon: Home, to: "/" },
  { key: "hr", moduleKey: "hr", labelKey: "nav.hr", icon: Users, to: "/hr" },
  { key: "tasks", moduleKey: "tasks", labelKey: "nav.tasks", icon: CheckSquare, to: "/tasks" },
  { key: "notes", moduleKey: "notes", labelKey: "nav.notes", icon: StickyNote, to: "/notes" },
];

function navLabel(t, key) {
  const val = t(key);
  if (val && val !== key) return val;
  const leaf = key.split(".").pop() || key;
  return leaf.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
}

function MobileTopBar({ onOpenMore }) {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const { setLanguage } = useAuth();

  const switchLang = async () => {
    const next = i18n.language?.startsWith("hi") ? "en" : "hi";
    await i18n.changeLanguage(next);
    setLanguage(next);
  };

  return (
    <header
      data-testid="mobile-topbar"
      className="fixed top-0 left-0 right-0 z-30 h-12 glass border-b border-[var(--surface-border)] flex items-center gap-2 px-3"
      style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(3rem + env(safe-area-inset-top))" }}
    >
      <Link to="/" data-testid="mobile-topbar-logo" className="flex items-center gap-2 flex-1 min-w-0">
        <Logo size={22} kind="mark" />
        <span className="font-heading text-[11px] font-bold tracking-widest truncate">
          SHARDA ENERGY OS
        </span>
      </Link>

      <InstallAppButton variant="topbar" />

      {/* Language + theme collapsed into icon buttons -- 48px height keeps
          the bar honest, so we lose the full labels but keep the toggles. */}
      <button
        onClick={switchLang}
        data-testid="mobile-lang-toggle"
        title="Toggle language"
        aria-label="Toggle language"
        className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/5 border border-[var(--surface-border)] hover:bg-white/10"
      >
        <Languages className="h-3.5 w-3.5" />
      </button>

      <button
        onClick={toggle}
        data-testid="mobile-theme-toggle"
        title="Toggle theme"
        aria-label="Toggle theme"
        className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/5 border border-[var(--surface-border)] hover:bg-white/10"
      >
        {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
      </button>

      <NotificationsBell />
      <ProfileMenu />
    </header>
  );
}

function MobileBottomTabBar({ moreTabs }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <nav
      data-testid="mobile-bottom-tab-bar"
      className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-[var(--surface-border)] flex items-stretch"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        height: "calc(3.5rem + env(safe-area-inset-bottom))",
      }}
    >
      {MOBILE_TABS.map((tab) => {
        const Icon = tab.icon;
        const active = tab.key === "home" ? loc.pathname === "/" : loc.pathname.startsWith(tab.to);
        return (
          <Link
            key={tab.key}
            to={tab.to}
            data-testid={`mobile-tab-${tab.key}`}
            className={[
              "flex-1 flex flex-col items-center justify-center gap-0.5 relative select-none",
              active ? "text-sky" : "text-foreground/70",
            ].join(" ")}
          >
            {active && (
              <span
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-gradient-primary"
              />
            )}
            <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
            <span className="text-[10px] uppercase tracking-wider">{navLabel(t, tab.labelKey)}</span>
          </Link>
        );
      })}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetTrigger asChild>
          <button
            data-testid="mobile-tab-more"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 text-foreground/70 select-none"
            aria-label="More modules"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-[10px] uppercase tracking-wider">{t("common.more", "More")}</span>
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          data-testid="mobile-more-sheet"
          className="bg-card/95 backdrop-blur-xl rounded-t-2xl max-h-[80vh] flex flex-col"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center justify-between px-1 pt-1 pb-3">
            <div className="font-heading text-sm font-bold tracking-widest">MORE</div>
            {/* Radix Sheet provides its own Close X in the corner; no custom
                close button needed here. */}
          </div>
          <div className="overflow-y-auto flex-1">
            <div className="grid grid-cols-3 gap-2">
              {moreTabs.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.key}
                    onClick={() => {
                      setMoreOpen(false);
                      navigate(m.to);
                    }}
                    data-testid={`mobile-more-item-${m.key}`}
                    className="flex flex-col items-center justify-center gap-1.5 rounded-xl p-3 bg-white/[0.03] hover:bg-white/[0.08] border border-[var(--surface-border)] transition-colors"
                  >
                    <Icon className="h-5 w-5 text-sky" />
                    <span className="text-[11px] text-center text-foreground/90 leading-tight">
                      {t(m.labelKey)}
                    </span>
                    {!m.enabled && (
                      <Badge variant="secondary" className="text-[9px] bg-white/10 border-0 px-1.5 py-0">
                        P{m.phase}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}

export function MobileShell() {
  const loc = useLocation();
  const { theme } = useTheme();
  const user = useAuth((s) => s.user);
  const { loaded, refresh, reset } = useModuleVisibility();
  const canUsers = usePermission("users", "view");
  const canAudit = usePermission("audit", "view");
  const canViewAdmin = canUsers || canAudit;
  const modules = useModuleVisibility((s) => s.previewModules || s.modules);

  const prevUidRef = React.useRef(null);
  useEffect(() => {
    if (user?.id && prevUidRef.current && prevUidRef.current !== user.id) {
      reset();
    }
    prevUidRef.current = user?.id || null;
    if (user?.id && !loaded) refresh();
  }, [user?.id, loaded, refresh, reset]);

  // Secondary modules for the "More" sheet: everything from NAV except the
  // 4 primary tabs. RBAC + module visibility identical to the sidebar.
  const primaryKeys = new Set(MOBILE_TABS.map((t) => t.key));
  const moreTabs = NAV.filter((m) => {
    if (primaryKeys.has(m.key)) return false;
    if (m.key === "admin" && !canViewAdmin) return false;
    // Chat god view is CEO-only -- respected via `showForRoles`.
    if (m.showForRoles && !m.showForRoles.includes(user?.role)) return false;
    if (!loaded || !modules) return true;
    if (!m.moduleKey) return true;
    return modules[m.moduleKey] === true;
  });

  return (
    <div
      data-testid="mobile-shell"
      className={["min-h-screen", theme === "dark" ? "app-bg-dark" : "app-bg-light"].join(" ")}
    >
      <MobileTopBar />
      <PreviewBanner />
      <PasswordChangeBanner />
      <main
        data-testid="app-main"
        className="px-3"
        style={{
          paddingTop: "calc(3.25rem + env(safe-area-inset-top))",
          paddingBottom: "calc(4rem + env(safe-area-inset-bottom))",
        }}
      >
        <div key={loc.pathname} className="animate-shell-in">
          <ModuleGuard>
            <Outlet />
          </ModuleGuard>
        </div>
      </main>
      <MobileBottomTabBar moreTabs={moreTabs} />
      <HelpBotLauncher />
      <GlobalCommandPalette />
      <QuickCaptureModal />
    </div>
  );
}

export default MobileShell;
